import { ExcelUploadsFilesList } from '@futrli-sdk/predict';

import { ForecastTemplateGenerationPayload } from '../predict/src/core/api/forecast';
import { ApiView, ApiViewState, getDefaultApiViewState } from './core/ApiView';
import { excelFilesQuery, excelFilesQueryKey } from './queries/excelFiles';
import { generateDriverTemplateMutation, generateForecastTemplateMutation } from './mutations/excelFiles';
import { queryClient } from './core/queryClient';

export type ExcelEntityConfig =
  | { entityType: 'driver' }
  | { entityType: 'forecast'; forecastId: string }
  | { entityType: 'organisation' };

export interface ExcelFilesViewState extends ApiViewState {
  errorGeneratingTemplate: Error | null;
  files: ExcelUploadsFilesList[];
  isFetchingFiles: boolean;
  isGeneratingTemplate: boolean;
  templateUrl?: string;
}

function getQueryParams(config: ExcelEntityConfig): Record<string, string> {
  switch (config.entityType) {
    case 'driver':
      return { file_type: 'driver' };
    case 'forecast':
      return { forecast_id: config.forecastId };
    case 'organisation':
    default:
      return {};
  }
}

export class ExcelFilesView extends ApiView {
  private excelFilesObs: ReturnType<typeof excelFilesQuery>;
  private queryParams: Record<string, string>;

  private driverTemplateMutation: ReturnType<typeof generateDriverTemplateMutation> | null = null;
  private forecastTemplateMutation: ReturnType<typeof generateForecastTemplateMutation> | null = null;

  public state: ExcelFilesViewState = {
    ...getDefaultApiViewState(),
    errorGeneratingTemplate: null,
    files: [],
    isFetchingFiles: false,
    isGeneratingTemplate: false,
    templateUrl: undefined
  };

  constructor(private workspaceId: string, private organisationId: string, private entityConfig: ExcelEntityConfig) {
    super();

    this.queryParams = getQueryParams(entityConfig);

    // Observers
    this.excelFilesObs = this.addQuery(excelFilesQuery(workspaceId, organisationId, this.queryParams));

    // Mutations
    if (entityConfig.entityType === 'driver') {
      this.driverTemplateMutation = this.addMutation(generateDriverTemplateMutation(workspaceId, organisationId));
    }
    if (entityConfig.entityType === 'forecast') {
      this.forecastTemplateMutation = this.addMutation(generateForecastTemplateMutation(workspaceId, organisationId));
    }

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public generateDriverTemplate() {
    if (this.files.length) {
      return;
    }
    return this.driverTemplateMutation?.mutate(undefined as void);
  }

  public generateForecastTemplate(data: ForecastTemplateGenerationPayload) {
    if (this.files.length) {
      return;
    }
    return this.forecastTemplateMutation?.mutate(data);
  }

  public invalidateFiles() {
    return queryClient.invalidateQueries({
      queryKey: excelFilesQueryKey(this.workspaceId, this.organisationId, this.queryParams)
    });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get errorGeneratingTemplate() {
    return (
      this.driverTemplateMutation?.getCurrentResult().error ??
      this.forecastTemplateMutation?.getCurrentResult().error ??
      null
    );
  }

  private get files() {
    return this.getResult(this.excelFilesObs).data?.files ?? [];
  }

  protected getNewState(): ExcelFilesViewState {
    return {
      ...super.getNewState(),
      errorGeneratingTemplate: this.errorGeneratingTemplate,
      files: this.files,
      isFetchingFiles: this.isFetchingFiles,
      isGeneratingTemplate: this.isGeneratingTemplate,
      templateUrl: this.templateUrl
    };
  }

  private get isFetchingFiles() {
    const result = this.getResult(this.excelFilesObs);
    return result.isLoading || !result.data;
  }

  private get isGeneratingTemplate() {
    return (
      (this.driverTemplateMutation?.getCurrentResult().isPending ?? false) ||
      (this.forecastTemplateMutation?.getCurrentResult().isPending ?? false)
    );
  }

  private get templateUrl() {
    return (
      this.driverTemplateMutation?.getCurrentResult().data?.template_url ??
      this.forecastTemplateMutation?.getCurrentResult().data?.template_url
    );
  }
}
